import { useMemo } from 'react';
import { TFunction } from 'i18next';

import { GroupingKey } from 'events/components/FilterGroupingControl/_types';
import { Event } from 'events/_types';
import isEventResolved from 'events/helpers/isEventResolved';
import { URGENCY_KEY_ORDER, getUrgencyLabel } from 'dashboard/urgencyColors';

export interface EventGroup {
  key: string;
  label: string | null;
  events: Event[];
}

const URGENCY_KEY_FALLBACK = 'Unspecified';

export default function useEventGroups(
  events: Event[],
  grouping: GroupingKey,
  t: TFunction
): EventGroup[] {
  return useMemo<EventGroup[]>(() => {
    if (events.length === 0) return [];

    const sortedEvents = [...events].sort(
      (a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime()
    );
    const pinnedEvents = sortedEvents.filter((e) => e.pinned);
    const unpinnedEvents = sortedEvents.filter((e) => !e.pinned);

    const pinnedGroup: EventGroup | null = pinnedEvents.length
      ? {
          key: 'pinned',
          label: t('eventGroups.pinned'),
          events: pinnedEvents,
        }
      : null;

    const withPinned = (groups: EventGroup[]): EventGroup[] =>
      pinnedGroup ? [pinnedGroup, ...groups] : groups;

    if (grouping === 'urgency') {
      const buckets = new Map<string, Event[]>();
      unpinnedEvents.forEach((event) => {
        const key = event.urgency_key ?? URGENCY_KEY_FALLBACK;
        const bucket = buckets.get(key) ?? [];
        bucket.push(event);
        buckets.set(key, bucket);
      });
      const ordered: EventGroup[] = [];
      URGENCY_KEY_ORDER.forEach((key) => {
        const bucket = buckets.get(key);
        if (bucket?.length) {
          ordered.push({ key, label: getUrgencyLabel(key, t), events: bucket });
          buckets.delete(key);
        }
      });
      buckets.forEach((bucket, key) => {
        ordered.push({ key, label: getUrgencyLabel(key, t), events: bucket });
      });
      return withPinned(ordered);
    }

    if (grouping === 'status') {
      const open = unpinnedEvents.filter((e) => !isEventResolved(e.status));
      const resolved = unpinnedEvents.filter((e) => e.status === 'resolved');
      const autoResolved = unpinnedEvents.filter(
        (e) => e.status === 'auto_resolved'
      );
      const groups: EventGroup[] = [];
      if (open.length)
        groups.push({
          key: 'open',
          label: t('eventSummary.status.open'),
          events: open,
        });
      if (resolved.length)
        groups.push({
          key: 'resolved',
          label: t('eventSummary.status.resolved'),
          events: resolved,
        });
      if (autoResolved.length)
        groups.push({
          key: 'auto_resolved',
          label: t('eventSummary.status.auto_resolved'),
          events: autoResolved,
        });
      return withPinned(groups);
    }

    if (grouping === 'visibility') {
      const shared = unpinnedEvents.filter((e) => !e.is_private);
      const priv = unpinnedEvents.filter((e) => e.is_private);
      const groups: EventGroup[] = [];
      if (shared.length)
        groups.push({
          key: 'shared',
          label: t('filterGrouping.visibility.options.shared'),
          events: shared,
        });
      if (priv.length)
        groups.push({
          key: 'private',
          label: t('filterGrouping.visibility.options.private'),
          events: priv,
        });
      return withPinned(groups);
    }

    if (pinnedGroup && unpinnedEvents.length) {
      return [
        pinnedGroup,
        {
          key: 'events',
          label: t('eventGroups.events'),
          events: unpinnedEvents,
        },
      ];
    }
    if (pinnedGroup) return [pinnedGroup];

    return [{ key: 'all', label: null, events: unpinnedEvents }];
  }, [events, grouping, t]);
}
